import { Injectable } from '@angular/core';
import { ChecklistDetailDto, ChecklistSectionDto, ChecklistItemDetailDto } from '../models/Checklist';
import {
  CHECKLIST_ACCESSORY_TEMPLATE_IDS,
  CHECKLIST_TEMPLATE_IDS,
  buildTemplateIdToFieldName,
  getAccessoryGroupTitleByTemplateId,
  isChecklistPdfAttachment
} from '../config/checklist-template.config';
// @ts-ignore
import jsPDF from 'jspdf';

const T = CHECKLIST_TEMPLATE_IDS;

@Injectable({
  providedIn: 'root'
})
export class ChecklistPdfService {

  private templateIdToFieldName: Record<number, string> = buildTemplateIdToFieldName();

  /**
   * Converte PDF Blob para base64
   */
  async pdfBlobToBase64(pdfBlob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result as string;
        // Extrair apenas a parte base64 (sem o prefixo data:application/pdf;base64,)
        const base64Only = base64.split(',')[1] || base64;
        resolve(base64Only);
      };
      reader.onerror = reject;
      reader.readAsDataURL(pdfBlob);
    });
  }

  /**
   * Gera PDF completo do checklist com todas as informações, fotos e assinatura
   */
  async generateChecklistPDF(checklist: ChecklistDetailDto): Promise<Blob> {
    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4'
    });

    let yPosition = 20;
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 15;
    const contentWidth = pageWidth - (margin * 2);
    const lineHeight = 7;
    const sectionSpacing = 10;

    // Cabeçalho com fundo colorido (sem ID e Tipo)
    doc.setFillColor(43, 58, 103); // Cor #2b3a67
    doc.rect(0, 0, pageWidth, 30, 'F');
    
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    doc.text('CHECKLIST DE INSPEÇÃO DE VEÍCULO', pageWidth / 2, 18, { align: 'center' });
    
    yPosition = 38;
    doc.setTextColor(0, 0, 0);

    // Data e Hora em destaque
    if (checklist.ChecklistDate) {
      const date = new Date(checklist.ChecklistDate);
      const dateStr = date.toLocaleDateString('pt-BR');
      const timeStr = date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text(`Data da Inspeção: ${dateStr} - Hora: ${timeStr}`, margin, yPosition);
      yPosition += 7;
    }

    // Linha separadora
    doc.setLineWidth(0.8);
    doc.setDrawColor(43, 58, 103);
    doc.line(margin, yPosition, pageWidth - margin, yPosition);
    yPosition += sectionSpacing + 3;

    // Informações do Veículo com box destacado
    const vehicleInfo = this.extractVehicleInfo(checklist);
    if (yPosition > pageHeight - 50) {
      doc.addPage();
      yPosition = 20;
    }

    doc.setFillColor(240, 240, 240);
    doc.rect(margin - 2, yPosition - 5, contentWidth + 4, 35, 'F');
    
    doc.setFontSize(13);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(43, 58, 103);
    doc.text('INFORMAÇÕES DO VEÍCULO', margin, yPosition);
    yPosition += 8;

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(0, 0, 0);
    
    const vehicleFields = [
      { label: 'Placa', value: vehicleInfo.plate },
      { label: 'Modelo', value: vehicleInfo.model },
      { label: 'Quilometragem', value: vehicleInfo.mileage ? `${vehicleInfo.mileage.toLocaleString('pt-BR')} km` : 'N/A' },
      { label: 'Data do Checklist', value: vehicleInfo.checklistDate },
      { label: 'Hora do Checklist', value: vehicleInfo.checklistTime }
    ];

    // Layout em 2 colunas
    const col1X = margin;
    const col2X = margin + (contentWidth / 2) + 5;
    let col1Y = yPosition;
    let col2Y = yPosition;

    vehicleFields.forEach((field, index) => {
      if (col1Y > pageHeight - 30 || col2Y > pageHeight - 30) {
        doc.addPage();
        col1Y = 20;
        col2Y = 20;
        yPosition = 20;
      }
      const xPos = index % 2 === 0 ? col1X : col2X;
      const yPos = index % 2 === 0 ? col1Y : col2Y;
      doc.text(`${field.label}: ${field.value || 'N/A'}`, xPos, yPos);
      if (index % 2 === 0) col1Y += lineHeight;
      else col2Y += lineHeight;
    });

    yPosition = Math.max(col1Y, col2Y) + sectionSpacing;

    // Extrair informações do condutor das seções
    const driverInfo = this.extractDriverInfo(checklist.Sections);

    // Informações do Condutor com box destacado
    if (yPosition > pageHeight - 40) {
      doc.addPage();
      yPosition = 20;
    }

    doc.setFillColor(240, 240, 240);
    doc.rect(margin - 2, yPosition - 5, contentWidth + 4, 30, 'F');

    doc.setFontSize(13);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(43, 58, 103);
    doc.text('INFORMAÇÕES DO CONDUTOR', margin, yPosition);
    yPosition += 8;

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(0, 0, 0);
    
    // Layout em 2 colunas para dados do condutor
    const condCol1X = margin;
    const condCol2X = margin + (contentWidth / 2) + 5;
    let condY = yPosition;
    
    if (driverInfo.name) {
      doc.text(`Nome: ${driverInfo.name}`, condCol1X, condY);
      condY += lineHeight;
    }
    if (driverInfo.cpf) {
      doc.text(`CPF: ${driverInfo.cpf}`, condCol1X, condY);
      condY += lineHeight;
    }
    if (driverInfo.phone) {
      doc.text(`Telefone: ${driverInfo.phone}`, condCol2X, yPosition);
      yPosition += lineHeight;
    }
    if (driverInfo.email) {
      doc.text(`E-mail: ${driverInfo.email}`, condCol2X, yPosition);
      yPosition += lineHeight;
    }
    
    yPosition = Math.max(condY, yPosition) + sectionSpacing;

    // Seções do Checklist - Processar de forma organizada
    if (checklist.Sections && checklist.Sections.length > 0) {
      // Separar itens por tipo
      const allItems: ChecklistItemDetailDto[] = [];
      checklist.Sections.forEach(section => {
        if (section.Items) {
          allItems.push(...section.Items);
        }
      });

      // 1. CONDIÇÕES GERAIS (apenas: KM, Pneus, Combustível, Sujo)
      const conditionsItems = allItems.filter(
        (item) =>
          item.ChecklistTemplateItemId !== null &&
          (item.ChecklistTemplateItemId === T.panelKm ||
            item.ChecklistTemplateItemId === T.tireCondition ||
            item.ChecklistTemplateItemId === T.fuelLevel ||
            item.ChecklistTemplateItemId === T.isDirty)
      );

      if (conditionsItems.length > 0) {
        if (yPosition > pageHeight - 50) {
          doc.addPage();
          yPosition = 20;
        }

        doc.setFillColor(245, 245, 245);
        doc.rect(margin - 2, yPosition - 5, contentWidth + 4, 12, 'F');
        
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(43, 58, 103);
        doc.text('CONDIÇÕES GERAIS', margin, yPosition);
        yPosition += 10;
        
        doc.setTextColor(0, 0, 0);
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');

        for (const item of conditionsItems) {
          if (yPosition > pageHeight - 30) {
            doc.addPage();
            yPosition = 20;
          }
          const fieldName = this.getFieldName(item);
          if (item.NumberValue !== null && item.NumberValue !== undefined) {
            doc.text(`${fieldName}: ${item.NumberValue.toLocaleString('pt-BR')} km`, margin, yPosition);
          } else if (item.Condition) {
            doc.text(`${fieldName}: ${item.Condition}`, margin, yPosition);
          } else if (item.BooleanValue !== null) {
            doc.text(`${fieldName}: ${item.BooleanValue ? 'Sim' : 'Não'}`, margin, yPosition);
          }
          yPosition += lineHeight;
        }
        yPosition += sectionSpacing;
      }

      // 2. SEÇÃO FOTOS (obrigatórias e detalhadas)
      const photoItems = allItems.filter(
        (item) =>
          item.PhotoUrl &&
          item.ChecklistTemplateItemId !== null &&
          !isChecklistPdfAttachment(item.ChecklistTemplateItemId, item.PhotoUrl) &&
          item.ChecklistTemplateItemId !== T.signaturePhoto &&
          ((item.ChecklistTemplateItemId >= T.photoFront && item.ChecklistTemplateItemId <= T.photoBack) ||
            item.ChecklistTemplateItemId === T.detailPhoto)
      );

      if (photoItems.length > 0) {
        if (yPosition > pageHeight - 50) {
          doc.addPage();
          yPosition = 20;
        }

        doc.setFillColor(245, 245, 245);
        doc.rect(margin - 2, yPosition - 5, contentWidth + 4, 12, 'F');
        
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(43, 58, 103);
        doc.text('FOTOS', margin, yPosition);
        yPosition += 10;
        
        doc.setTextColor(0, 0, 0);
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');

        // Separar fotos obrigatórias e detalhadas
        // Mapear template IDs para lados e garantir apenas uma foto por lado
        const templateIdToSide: { [key: number]: string } = {
          [T.photoFront]: 'frente',
          [T.photoRight]: 'direita',
          [T.photoLeft]: 'esquerda',
          [T.photoTop]: 'cima',
          [T.photoBack]: 'traseira'
        };

        const requiredPhotosMap = new Map<number, ChecklistItemDetailDto>();
        photoItems.forEach(item => {
          if (
            item.ChecklistTemplateItemId !== null &&
            item.ChecklistTemplateItemId >= T.photoFront &&
            item.ChecklistTemplateItemId <= T.photoBack
          ) {
            // Se já existe uma foto para este template ID, manter a primeira
            if (!requiredPhotosMap.has(item.ChecklistTemplateItemId)) {
              requiredPhotosMap.set(item.ChecklistTemplateItemId, item);
            }
          }
        });

        // Converter map para array ordenado (34-38)
        const requiredPhotos = Array.from(requiredPhotosMap.values())
          .sort((a, b) => (a.ChecklistTemplateItemId || 0) - (b.ChecklistTemplateItemId || 0));

        const detailPhotos = photoItems.filter(
          (item) => item.ChecklistTemplateItemId !== null && item.ChecklistTemplateItemId === T.detailPhoto
        );

        // Fotos obrigatórias (uma por lado com legenda correta)
        if (requiredPhotos.length > 0) {
          doc.setFontSize(11);
          doc.setFont('helvetica', 'bold');
          doc.text('Fotos por Lado:', margin, yPosition);
          yPosition += 7;
          doc.setFontSize(10);
          doc.setFont('helvetica', 'normal');

          for (const item of requiredPhotos) {
            if (yPosition > pageHeight - 70) {
              doc.addPage();
              yPosition = 20;
            }
            try {
              // Criar uma cópia do item para não modificar o original
              const photoItem = { ...item };
              
              // Garantir que o PhotoSide está correto baseado no template ID
              if (photoItem.ChecklistTemplateItemId !== null && templateIdToSide[photoItem.ChecklistTemplateItemId]) {
                const correctSide = templateIdToSide[photoItem.ChecklistTemplateItemId];
                // Mapear para o formato esperado
                const sideMap: { [key: string]: string } = {
                  'frente': 'Front',
                  'direita': 'Right',
                  'esquerda': 'Left',
                  'cima': 'Top',
                  'traseira': 'Back'
                };
                photoItem.PhotoSide = sideMap[correctSide] || photoItem.PhotoSide;
              }
              
              await this.addPhotoToPDF(doc, photoItem, margin, yPosition, contentWidth);
              yPosition += 65;
            } catch (error) {
              yPosition += lineHeight;
            }
          }
          yPosition += 5;
        }

        // Fotos detalhadas (com condição e observação)
        if (detailPhotos.length > 0) {
          doc.setFontSize(11);
          doc.setFont('helvetica', 'bold');
          doc.text('Fotos Detalhadas:', margin, yPosition);
          yPosition += 7;
          doc.setFontSize(10);
          doc.setFont('helvetica', 'normal');

          for (const item of detailPhotos) {
            if (yPosition > pageHeight - 70) {
              doc.addPage();
              yPosition = 20;
            }
            try {
              await this.addPhotoToPDF(doc, item, margin, yPosition, contentWidth);
              yPosition += 65;
            } catch (error) {
              yPosition += lineHeight;
            }
          }
        }
        yPosition += sectionSpacing;
      }

      // 3. ACESSÓRIOS (todos alinhados)
      const accessoriesItems = allItems.filter(
        (item) =>
          item.ChecklistTemplateItemId !== null &&
          CHECKLIST_ACCESSORY_TEMPLATE_IDS.includes(item.ChecklistTemplateItemId)
      );

      if (accessoriesItems.length > 0) {
        if (yPosition > pageHeight - 50) {
          doc.addPage();
          yPosition = 20;
        }

        doc.setFillColor(245, 245, 245);
        doc.rect(margin - 2, yPosition - 5, contentWidth + 4, 12, 'F');

        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(43, 58, 103);
        doc.text('ACESSÓRIOS / EQUIPAMENTOS', margin, yPosition);
        yPosition += 10;

        const groupedAccessories = new Map<string, ChecklistItemDetailDto[]>();
        for (const item of accessoriesItems) {
          const groupTitle =
            item.ChecklistTemplateItemId != null
              ? getAccessoryGroupTitleByTemplateId(item.ChecklistTemplateItemId)
              : 'Outros';
          if (!groupedAccessories.has(groupTitle)) {
            groupedAccessories.set(groupTitle, []);
          }
          groupedAccessories.get(groupTitle)!.push(item);
        }

        for (const [groupTitle, groupItems] of groupedAccessories) {
          if (yPosition > pageHeight - 30) {
            doc.addPage();
            yPosition = 20;
          }

          doc.setTextColor(43, 58, 103);
          doc.setFontSize(10);
          doc.setFont('helvetica', 'bold');
          doc.text(groupTitle, margin, yPosition);
          yPosition += 6;

          doc.setTextColor(0, 0, 0);
          doc.setFontSize(9);
          doc.setFont('helvetica', 'normal');

          const colWidth = contentWidth / 2;
          const col1X = margin;
          const col2X = margin + colWidth + 4;
          let col1Y = yPosition;
          let col2Y = yPosition;

          groupItems.forEach((item, index) => {
            const col = index % 2;
            const xPos = col === 0 ? col1X : col2X;
            let yPos = col === 0 ? col1Y : col2Y;

            if (yPos > pageHeight - 20) {
              doc.addPage();
              col1Y = 20;
              col2Y = 20;
              yPos = 20;
            }

            const fieldName = this.getFieldName(item);
            const value = item.Condition || (item.BooleanValue != null ? (item.BooleanValue ? 'Sim' : 'Não') : '');
            doc.text(`${fieldName}: ${value}`, xPos, yPos);

            if (col === 0) {
              col1Y += lineHeight;
            } else {
              col2Y += lineHeight;
            }
          });

          yPosition = Math.max(col1Y, col2Y) + 4;
        }

        yPosition += sectionSpacing;
      }

      // 4. OBSERVAÇÕES
      const observationsItem = allItems.find(
        (item) => item.ChecklistTemplateItemId !== null && item.ChecklistTemplateItemId === T.observations
      );
      if (observationsItem && observationsItem.TextValue) {
        if (yPosition > pageHeight - 50) {
          doc.addPage();
          yPosition = 20;
        }

        doc.setFillColor(245, 245, 245);
        doc.rect(margin - 2, yPosition - 5, contentWidth + 4, 12, 'F');
        
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(43, 58, 103);
        doc.text('OBSERVAÇÕES', margin, yPosition);
        yPosition += 10;
        
        doc.setTextColor(0, 0, 0);
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        const obsLines = doc.splitTextToSize(observationsItem.TextValue, contentWidth);
        doc.text(obsLines, margin, yPosition);
        yPosition += (obsLines.length * lineHeight) + sectionSpacing;
      }
    }

    // Assinatura Digital (se houver) - apenas uma vez, no final
    const signatureItem = this.findSignatureItem(checklist.Sections);
    if (signatureItem && signatureItem.PhotoUrl) {
      const signatureBlockHeight = 72;
      if (yPosition > pageHeight - signatureBlockHeight - 15) {
        doc.addPage();
        yPosition = 20;
      }

      const blockTop = yPosition - 5;
      doc.setFillColor(250, 250, 250);
      doc.rect(margin - 2, blockTop, contentWidth + 4, signatureBlockHeight, 'F');
      doc.setDrawColor(200, 200, 200);
      doc.setLineWidth(0.5);
      doc.rect(margin - 2, blockTop, contentWidth + 4, signatureBlockHeight, 'S');

      try {
        yPosition = await this.addSignatureToPDF(
          doc,
          signatureItem,
          margin,
          yPosition,
          contentWidth,
          pageWidth
        );
        yPosition += 8;
      } catch {
        doc.setFontSize(13);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(43, 58, 103);
        doc.text('ASSINATURA DIGITAL', pageWidth / 2, yPosition + 6, { align: 'center' });
        yPosition += signatureBlockHeight;
      }
    }

    // Rodapé
    const totalPages = doc.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setFont('helvetica', 'italic');
      doc.text(
        `Página ${i} de ${totalPages} - Gerado em ${new Date().toLocaleString('pt-BR')}`,
        pageWidth / 2,
        pageHeight - 10,
        { align: 'center' }
      );
    }

    // Converter para Blob
    const pdfBlob = doc.output('blob');
    return pdfBlob;
  }

  /**
   * Renderiza assinatura centralizada no bloco do PDF (com recorte de margens vazias).
   */
  private async addSignatureToPDF(
    doc: jsPDF,
    item: ChecklistItemDetailDto,
    margin: number,
    yStart: number,
    contentWidth: number,
    pageWidth: number
  ): Promise<number> {
    if (!item.PhotoUrl) {
      return yStart;
    }

    const trimmed = await this.trimSignatureWhitespace(item.PhotoUrl);
    const sigBoxWidth = Math.min(120, contentWidth * 0.72);
    const sigBoxHeight = 34;
    const sigBoxX = margin + (contentWidth - sigBoxWidth) / 2;
    const centerX = pageWidth / 2;

    doc.setFontSize(13);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(43, 58, 103);
    doc.text('ASSINATURA DIGITAL', centerX, yStart + 5, { align: 'center' });

    const label = item.ItemName || 'Assinatura do Condutor';
    doc.setFontSize(10);
    doc.text(label, centerX, yStart + 12, { align: 'center' });

    const boxY = yStart + 16;
    doc.setFillColor(255, 255, 255);
    doc.setDrawColor(190, 190, 190);
    doc.setLineWidth(0.35);
    doc.rect(sigBoxX, boxY, sigBoxWidth, sigBoxHeight, 'FD');

    const innerPad = 3;
    const maxImgW = sigBoxWidth - innerPad * 2;
    const maxImgH = sigBoxHeight - innerPad * 2;

    let imgWidth = maxImgW;
    let imgHeight = (trimmed.height * maxImgW) / trimmed.width;
    if (imgHeight > maxImgH) {
      imgHeight = maxImgH;
      imgWidth = (trimmed.width * maxImgH) / trimmed.height;
    }

    const imgX = sigBoxX + (sigBoxWidth - imgWidth) / 2;
    const imgY = boxY + (sigBoxHeight - imgHeight) / 2;
    const imageFormat = trimmed.dataUrl.includes('data:image/png') ? 'PNG' : 'JPEG';
    doc.addImage(trimmed.dataUrl, imageFormat, imgX, imgY, imgWidth, imgHeight);

    return boxY + sigBoxHeight + 4;
  }

  /**
   * Remove margens vazias ao redor do traço da assinatura (canvas largo com pouco conteúdo à esquerda).
   */
  private async trimSignatureWhitespace(
    photoUrl: string
  ): Promise<{ dataUrl: string; width: number; height: number }> {
    const img = await this.loadImage(photoUrl);
    const canvas = document.createElement('canvas');
    canvas.width = img.naturalWidth || img.width;
    canvas.height = img.naturalHeight || img.height;
    const ctx = canvas.getContext('2d');
    if (!ctx || canvas.width === 0 || canvas.height === 0) {
      return {
        dataUrl: img.src,
        width: img.width,
        height: img.height
      };
    }

    ctx.drawImage(img, 0, 0);
    const { data, width, height } = ctx.getImageData(0, 0, canvas.width, canvas.height);

    let minX = width;
    let minY = height;
    let maxX = 0;
    let maxY = 0;
    let hasInk = false;

    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const i = (y * width + x) * 4;
        const r = data[i];
        const g = data[i + 1];
        const b = data[i + 2];
        const a = data[i + 3];
        const isInk = a > 20 && (r + g + b) < 740;
        if (isInk) {
          hasInk = true;
          minX = Math.min(minX, x);
          minY = Math.min(minY, y);
          maxX = Math.max(maxX, x);
          maxY = Math.max(maxY, y);
        }
      }
    }

    if (!hasInk) {
      return { dataUrl: img.src, width: img.width, height: img.height };
    }

    const pad = Math.max(4, Math.round(Math.min(width, height) * 0.02));
    minX = Math.max(0, minX - pad);
    minY = Math.max(0, minY - pad);
    maxX = Math.min(width - 1, maxX + pad);
    maxY = Math.min(height - 1, maxY + pad);

    const cropW = maxX - minX + 1;
    const cropH = maxY - minY + 1;
    const cropped = document.createElement('canvas');
    cropped.width = cropW;
    cropped.height = cropH;
    const cropCtx = cropped.getContext('2d');
    if (!cropCtx) {
      return { dataUrl: img.src, width: img.width, height: img.height };
    }

    cropCtx.fillStyle = '#ffffff';
    cropCtx.fillRect(0, 0, cropW, cropH);
    cropCtx.drawImage(canvas, minX, minY, cropW, cropH, 0, 0, cropW, cropH);

    const dataUrl =
      photoUrl.toLowerCase().includes('png') || photoUrl.startsWith('iVBOR')
        ? cropped.toDataURL('image/png')
        : cropped.toDataURL('image/jpeg', 0.92);

    return { dataUrl, width: cropW, height: cropH };
  }

  /**
   * Adiciona foto ao PDF
   */
  private async addPhotoToPDF(
    doc: jsPDF,
    item: ChecklistItemDetailDto,
    x: number,
    y: number,
    maxWidth: number
  ): Promise<void> {
    if (!item.PhotoUrl) return;
    if (isChecklistPdfAttachment(item.ChecklistTemplateItemId, item.PhotoUrl)) return;

    try {
      // Converter base64 ou URL para imagem
      const img = await this.loadImage(item.PhotoUrl);
      
      // Calcular dimensões mantendo proporção
      const maxHeight = 60; // Aumentado para fotos maiores
      let imgWidth = maxWidth;
      let imgHeight = (img.height * maxWidth) / img.width;

      if (imgHeight > maxHeight) {
        imgHeight = maxHeight;
        imgWidth = (img.width * maxHeight) / img.height;
      }

      // Adicionar nome do item
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      const itemName = item.ItemName || 'Foto';
      doc.text(itemName, x, y);
      y += 6;

      // Adicionar imagem
      const imageFormat = item.PhotoUrl.includes('data:image/png') ? 'PNG' : 'JPEG';
      doc.addImage(img, imageFormat, x, y, imgWidth, imgHeight);

      // Adicionar informações adicionais se houver
      y += imgHeight + 3;
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      
      // Para fotos obrigatórias: apenas legenda do lado (mapear pelo template ID)
      if (
        item.ChecklistTemplateItemId !== null &&
        item.ChecklistTemplateItemId >= T.photoFront &&
        item.ChecklistTemplateItemId <= T.photoBack
      ) {
        const templateIdToSideName: { [key: number]: string } = {
          [T.photoFront]: 'Frente',
          [T.photoRight]: 'Direita',
          [T.photoLeft]: 'Esquerda',
          [T.photoTop]: 'Cima',
          [T.photoBack]: 'Traseira'
        };
        const sideName = templateIdToSideName[item.ChecklistTemplateItemId] || 'Lado';
        doc.setFont('helvetica', 'bold');
        doc.text(`Lado: ${sideName}`, x, y);
      }
      // Para fotos detalhadas: condição e observação
      else if (item.ChecklistTemplateItemId !== null && item.ChecklistTemplateItemId === T.detailPhoto) {
        if (item.PhotoSide) {
          doc.text(`Lado: ${this.mapPhotoSide(item.PhotoSide)}`, x, y);
          y += 4;
        }
        
        if (item.Condition) {
          doc.setFont('helvetica', 'bold');
          doc.text(`Condição: ${item.Condition}`, x, y);
          y += 4;
        }
        
        if (item.Observations) {
          doc.setFont('helvetica', 'italic');
          doc.setFontSize(7);
          const obsLines = doc.splitTextToSize(`Observação: ${item.Observations}`, maxWidth);
          doc.text(obsLines, x, y);
        }
      }
    } catch (error) {
      console.error('Erro ao processar imagem:', error);
      throw error;
    }
  }

  /**
   * Carrega imagem de base64 ou URL
   */
  private loadImage(src: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      
      // Se for base64, usar diretamente
      if (src.startsWith('data:') || src.startsWith('/9j/') || src.startsWith('iVBOR')) {
        let dataUrl = src;
        if (!src.startsWith('data:')) {
          // Se for base64 puro, adicionar prefixo
          if (src.startsWith('/9j/')) {
            dataUrl = `data:image/jpeg;base64,${src}`;
          } else if (src.startsWith('iVBOR')) {
            dataUrl = `data:image/png;base64,${src}`;
          }
        }
        img.src = dataUrl;
      } else {
        img.src = src;
      }

      img.onload = () => resolve(img);
      img.onerror = (error) => reject(error);
    });
  }

  /**
   * Extrai informações do veículo das seções
   */
  private extractVehicleInfo(checklist: ChecklistDetailDto): any {
    const sections = checklist.Sections || [];
    let plate = checklist.VehiclePlate || '';
    let model = '';
    let mileage = checklist.Mileage || null;
    let checklistDate = '';
    let checklistTime = '';

    for (const section of sections) {
      if (section.Items) {
        for (const item of section.Items) {
          if (item.ChecklistTemplateItemId === T.plate && item.TextValue) {
            plate = item.TextValue;
          } else if (item.ChecklistTemplateItemId === T.model && item.TextValue) {
            model = item.TextValue;
          } else if (item.ChecklistTemplateItemId === T.odometer && item.NumberValue !== null) {
            mileage = item.NumberValue;
          } else if (item.ChecklistTemplateItemId === T.checklistDate && item.TextValue) {
            checklistDate = item.TextValue;
          } else if (item.ChecklistTemplateItemId === T.checklistTime && item.TextValue) {
            checklistTime = item.TextValue;
          }
        }
      }
    }

    return { plate, model, mileage, checklistDate, checklistTime };
  }

  /**
   * Encontra item de assinatura
   */
  private findSignatureItem(sections: ChecklistSectionDto[] | undefined): ChecklistItemDetailDto | null {
    if (!sections) return null;

    for (const section of sections) {
      if (section.Items) {
        const signatureItem = section.Items.find(
          (item) => item.ChecklistTemplateItemId === T.signaturePhoto && item.PhotoUrl
        );
        if (signatureItem) return signatureItem;
      }
    }

    return null;
  }

  /**
   * Formata status
   */
  private formatStatus(status: string): string {
    const statusMap: { [key: string]: string } = {
      'Draft': 'Rascunho',
      'InProgress': 'Em Progresso',
      'Completed': 'Concluído',
      'Cancelled': 'Cancelado',
      'Vigente': 'Vigente'
    };
    return statusMap[status] || status;
  }

  /**
   * Mapeia lado da foto
   */
  private mapPhotoSide(side: string): string {
    const sideMap: { [key: string]: string } = {
      'frente': 'Frente',
      'direita': 'Direita',
      'esquerda': 'Esquerda',
      'cima': 'Cima',
      'traseira': 'Traseira',
      'front': 'Frente',
      'right': 'Direita',
      'left': 'Esquerda',
      'top': 'Cima',
      'back': 'Traseira'
    };
    return sideMap[side.toLowerCase()] || side;
  }

  /**
   * Obtém o nome do campo baseado no Template ID ou ItemName
   */
  private getFieldName(item: ChecklistItemDetailDto): string {
    if (item.ChecklistTemplateItemId && this.templateIdToFieldName[item.ChecklistTemplateItemId]) {
      return this.templateIdToFieldName[item.ChecklistTemplateItemId];
    }
    return item.ItemName || 'Item';
  }

  /**
   * Extrai informações do condutor das seções
   */
  private extractDriverInfo(sections: ChecklistSectionDto[] | undefined): { name: string; cpf: string; phone: string; email: string } {
    const driverInfo = { name: '', cpf: '', phone: '', email: '' };
    
    if (!sections) return driverInfo;

    for (const section of sections) {
      if (section.Items) {
        for (const item of section.Items) {
          if (item.ChecklistTemplateItemId === T.driverName && item.TextValue) {
            driverInfo.name = item.TextValue;
          } else if (item.ChecklistTemplateItemId === T.driverPhone && item.TextValue) {
            driverInfo.phone = item.TextValue;
          } else if (item.ChecklistTemplateItemId === T.signatureEmail && item.TextValue) {
            driverInfo.email = item.TextValue;
          } else if (item.ChecklistTemplateItemId === T.driverCpf && item.TextValue) {
            driverInfo.cpf = item.TextValue;
          } else if (item.ChecklistTemplateItemId === T.signatureName && item.TextValue && !driverInfo.name) {
            driverInfo.name = item.TextValue;
          } else if (item.ChecklistTemplateItemId === T.signaturePhone && item.TextValue && !driverInfo.phone) {
            driverInfo.phone = item.TextValue;
          } else if (item.ChecklistTemplateItemId === T.signatureCpf && item.TextValue && !driverInfo.cpf) {
            driverInfo.cpf = item.TextValue;
          }
        }
      }
    }

    return driverInfo;
  }
}
